import { GameName, GameTime, HokmType, BackgammonType } from '../net/enums';

/**
 * Persian/RTL display helpers, mirroring FaConvert.cs (Fa.NumberToMoney) and the Unity label maps.
 */

const MONEY_SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Q', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'];

/**
 * Abbreviates a money value as K/M/B/T/… with one floored decimal, matching the hardened
 * server-side Utility.NumToMoney. Uses exact BigInt math so huge balances never lose precision.
 * Examples: 999 -> "999", 1500 -> "1.5K", 50_000_000 -> "50M", 9_007_199_254_740_993 -> "9Q".
 */
export function numberToMoney(input: bigint | number): string {
  let num = typeof input === 'bigint' ? input : BigInt(Math.trunc(input));
  if (num === 0n) return '0';
  const negative = num < 0n;
  const sign = negative ? '-' : '';
  const abs = negative ? -num : num;
  if (abs < 1000n) return sign + abs.toString();

  const magnitude = abs.toString().length - 1;
  let scale = Math.floor(magnitude / 3);
  if (scale >= MONEY_SUFFIXES.length) scale = MONEY_SUFFIXES.length - 1;

  const divisor = 10n ** BigInt(scale * 3);
  const tenths = (abs * 10n) / divisor; // floor to one decimal place
  const whole = tenths / 10n;
  const frac = tenths % 10n;
  const formatted = frac === 0n ? whole.toString() : `${whole}.${frac}`;
  return sign + formatted + MONEY_SUFFIXES[scale];
}

const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

/** Converts ASCII digits in a string (or a number) to Persian digits. */
export function toPersianDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]);
}

// Unicode bidi isolates. Wrapping a left-to-right token (Persian digits followed by a Latin K/M/B/T
// magnitude suffix) in LRI…PDI keeps it a single contiguous run so the suffix can't be reordered to
// the wrong side when the token is embedded in surrounding RTL Persian text.
const LRI = '⁦'; // LEFT-TO-RIGHT ISOLATE
const RLI = '⁧'; // RIGHT-TO-LEFT ISOLATE
const PDI = '⁩'; // POP DIRECTIONAL ISOLATE

/** Wraps a left-to-right token so it renders intact inside RTL text. */
export function ltrIsolate(s: string): string {
  return `${LRI}${s}${PDI}`;
}

/** Wraps server-authored Persian copy so embedded Latin words cannot change the paragraph direction. */
export function rtlIsolate(s: string): string {
  return `${RLI}${s}${PDI}`;
}

/**
 * Isolate every already-wrapped line independently. Canvas implementations treat each newline as a
 * new bidi paragraph, so one isolate around the whole block can be lost after Safari wraps line one.
 */
export function rtlIsolateLines(lines: readonly string[]): string {
  return lines.map((line) => (line ? rtlIsolate(line) : '')).join('\n');
}

/** Clean up harmless server-authored spacing mistakes before measuring an RTL announcement. */
export function normalizeRtlAnnouncement(s: string): string {
  return s
    .replace(/\s+/gu, ' ')
    .replace(/خوش آمدید(?=\p{Script=Arabic})/gu, 'خوش آمدید ')
    .trim();
}

/**
 * Money formatted with Persian digits, isolated for RTL display (the lobby/table form). The isolate
 * keeps "۴۹.۹M" glued together — without it the Latin suffix is bidi-reordered away from its number
 * (e.g. the balance pill rendered "Mموجودی: ۴۹.۹"). Use numberToMoney for the raw, unisolated value.
 */
export function moneyFa(input: bigint | number): string {
  return ltrIsolate(toPersianDigits(numberToMoney(input)));
}

export function gameNameLabel(game: GameName): string {
  switch (game) {
    case GameName.Hokm:
      return 'حکم';
    case GameName.Backgammon:
      return 'تخته نرد';
    case GameName.Blackjack:
      return 'بیست و یک';
    default:
      return '';
  }
}

export function gameTimeLabel(time: GameTime): string {
  switch (time) {
    case GameTime.Relax:
      return 'آرام';
    case GameTime.Normal:
      return 'نرمال';
    case GameTime.Fast:
      return 'سریع';
    default:
      return '';
  }
}

/** Per-game variant label for GameInfoModel.GameType (Blackjack has no variant). */
export function gameTypeLabel(game: GameName, type: number): string {
  if (game === GameName.Hokm) {
    return type === HokmType.WithNaras ? 'نرس/سرس' : 'عادی';
  }
  if (game === GameName.Backgammon) {
    return type === BackgammonType.BackGammon ? 'گمون' : 'تخته';
  }
  return '';
}

/** "N دست" — the rounds/hands label used in the table list. */
export function roundsLabel(count: number): string {
  return `${toPersianDigits(count)} دست`;
}

/**
 * The search-list "count" segment: Blackjack tables show the bet («<money> بت», where GameCount is a
 * thousands-multiplier), card/board games show rounds («N دست»). Mirrors WaitListItem.cs.
 */
export function betOrRoundsLabel(game: GameName, count: number): string {
  if (game === GameName.Blackjack) return `${moneyFa(count * 1000)} بت`;
  return roundsLabel(count);
}
